import { reportsApi } from '../../../config/apiClient';

/** Tüm tutarlar KDV dahil ve TL. Gider satırları eksi işaretlidir. */
export interface ProfitLossLine {
  Key: string;
  Label: string;
  Amount: number;
  PercentOfNetSales: number | null;
  IsSubtotal: boolean;
  DrilldownAvailable: boolean;
  Children: ProfitLossLine[];
  /** Yalnız karşılaştırmalı raporda dolu. */
  CompareAmount?: number | null;
  /** Kalemde büyüklük değişimi, ara toplamda işaretli değişim (%). */
  ChangePercent?: number | null;
}

export type ProfitLossCompareMode = 'none' | 'previous' | 'lastYear';

export interface ProfitLossKpis {
  NetSales: number;
  GrossProfit: number;
  GrossMarginPercent: number | null;
  OperatingExpenses: number;
  OperatingProfit: number;
  NetProfit: number;
  NetMarginPercent: number | null;
  PrimeCostPercent: number | null;
}

export interface ProfitLossWarning {
  Code: string;
  Message: string;
  Amount: number | null;
  Count: number | null;
}

export interface ProfitLossStatement {
  PeriodStart: string;
  PeriodEnd: string;
  BranchIds: string[];
  IncludesUnbranched: boolean;
  /** Şube yöneticisi görünümü: şubesiz kayıtlar, banka, kredi kartı ve finansman yok. */
  ManagerView?: boolean;
  Lines: ProfitLossLine[];
  Kpis: ProfitLossKpis;
  Warnings: ProfitLossWarning[];
  GeneratedAt: string;
  CompareMode?: ProfitLossCompareMode;
  ComparePeriodStart?: string | null;
  ComparePeriodEnd?: string | null;
  CompareKpis?: ProfitLossKpis | null;
}

export interface ProfitLossBranchColumn {
  Key: string;
  Name: string;
  IsUnbranched: boolean;
  IsTotal: boolean;
  NetSales: number;
  NetProfit: number;
}

export interface ProfitLossBranchCell {
  Amount: number;
  PercentOfNetSales: number | null;
}

export interface ProfitLossBranchRow {
  Key: string;
  Label: string;
  Depth: number;
  IsSubtotal: boolean;
  /** Columns ile aynı sırada. */
  Values: ProfitLossBranchCell[];
}

export interface ProfitLossByBranch {
  PeriodStart: string;
  PeriodEnd: string;
  Columns: ProfitLossBranchColumn[];
  Rows: ProfitLossBranchRow[];
  Warnings: ProfitLossWarning[];
  GeneratedAt: string;
}

export interface ProfitLossTrendPoint {
  /** yyyy-MM */
  Month: string;
  NetSales: number;
  /** Pozitif tutar. */
  Cogs: number;
  GrossProfit: number;
  OperatingExpenses: number;
  OperatingProfit: number;
  NetProfit: number;
  GrossMarginPercent: number | null;
  NetMarginPercent: number | null;
}

export interface ProfitLossTrend {
  StartMonth: string;
  EndMonth: string;
  BranchIds: string[];
  Points: ProfitLossTrendPoint[];
  GeneratedAt: string;
}

export interface ProfitLossExpenseGroup {
  Key: string;
  Label: string;
  /** Pozitif = gider. */
  Amount: number;
  PercentOfExpenses: number | null;
  PercentOfNetSales: number | null;
}

export interface ProfitLossExpenseItem extends ProfitLossExpenseGroup {
  GroupKey: string;
  GroupLabel: string;
}

export interface ProfitLossExpenseBreakdown {
  NetSales: number;
  TotalExpenses: number;
  Groups: ProfitLossExpenseGroup[];
  TopItems: ProfitLossExpenseItem[];
  GeneratedAt: string;
}

export type ProfitLossDataQualityTarget =
  | 'purchaseInvoices' | 'salesInvoices' | 'cashbox' | 'bank' | 'creditCards' | 'personnel';

export interface ProfitLossDataQualityItem {
  Code: string;
  Message: string;
  Severity: 'warning' | 'info';
  Count: number;
  Amount: number | null;
  Target: ProfitLossDataQualityTarget;
}

export interface ProfitLossDataQuality {
  Items: ProfitLossDataQualityItem[];
  GeneratedAt: string;
}

export interface ProfitLossDrilldownRow {
  Date: string;
  SourceType: string;
  DocumentNo: string | null;
  Description: string | null;
  BranchId: string | null;
  BranchName: string | null;
  Amount: number;
  SourceId: string | null;
}

export interface ProfitLossDrilldownResult {
  LineKey: string;
  Items: ProfitLossDrilldownRow[];
  TotalCount: number;
  TotalAmount: number;
  Page: number;
  PageSize: number;
}

export interface ProfitLossQuery {
  startDate: Date;
  endDate: Date;
  branchIds: string[];
}

interface ApiResponse<T> {
  Success?: boolean;
  Data?: T;
  Message?: string;
}

/** Gün sonu raporuyla aynı sözleşme: yerel gün başı/sonu UTC ISO olarak gönderilir. */
const toParams = (q: ProfitLossQuery) => {
  const start = new Date(q.startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(q.endDate);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    branchIds: q.branchIds.length > 0 ? q.branchIds.join(',') : undefined,
  };
};

const unwrap = <T,>(body: ApiResponse<T> | undefined): T => {
  if (!body || body.Success === false || body.Data === undefined) {
    throw new Error(body?.Message ?? 'Beklenmeyen yanıt');
  }
  return body.Data;
};

export const getProfitLossStatement = async (
  q: ProfitLossQuery,
  compare: ProfitLossCompareMode = 'none',
): Promise<ProfitLossStatement> => {
  const res = await reportsApi.get<ApiResponse<ProfitLossStatement>>('/api/ProfitLoss/statement', {
    params: { ...toParams(q), compare: compare === 'none' ? undefined : compare },
    timeout: 120000,
  });
  return unwrap(res.data);
};

/**
 * Admin: şube filtresi uygulanmaz; tüm şubeler, Merkez (şubesiz) ve Toplam sütunu döner.
 * Yönetici (`withBranches`): yalnız seçili (kendi) şubeleri ve Toplam.
 */
export const getProfitLossByBranch = async (q: ProfitLossQuery, withBranches = false): Promise<ProfitLossByBranch> => {
  const { startDate, endDate, branchIds } = toParams(q);
  const res = await reportsApi.get<ApiResponse<ProfitLossByBranch>>('/api/ProfitLoss/by-branch', {
    params: { startDate, endDate, branchIds: withBranches ? branchIds : undefined },
    timeout: 180000,
  });
  return unwrap(res.data);
};

/** Ay değerleri yyyy-MM; en fazla 12 ay. */
export const getProfitLossTrend = async (
  startMonth: string,
  endMonth: string,
  branchIds: string[],
): Promise<ProfitLossTrend> => {
  const res = await reportsApi.get<ApiResponse<ProfitLossTrend>>('/api/ProfitLoss/trend', {
    params: { startMonth, endMonth, branchIds: branchIds.length > 0 ? branchIds.join(',') : undefined },
    timeout: 300000,
  });
  return unwrap(res.data);
};

export const getProfitLossExpenseBreakdown = async (q: ProfitLossQuery): Promise<ProfitLossExpenseBreakdown> => {
  const res = await reportsApi.get<ApiResponse<ProfitLossExpenseBreakdown>>('/api/ProfitLoss/expense-breakdown', {
    params: toParams(q),
    timeout: 120000,
  });
  return unwrap(res.data);
};

export const getProfitLossDataQuality = async (q: ProfitLossQuery): Promise<ProfitLossDataQuality> => {
  const res = await reportsApi.get<ApiResponse<ProfitLossDataQuality>>('/api/ProfitLoss/data-quality', {
    params: toParams(q),
    timeout: 120000,
  });
  return unwrap(res.data);
};

export const getProfitLossDrilldown = async (
  q: ProfitLossQuery,
  lineKey: string,
  page: number,
  pageSize: number,
): Promise<ProfitLossDrilldownResult> => {
  const res = await reportsApi.get<ApiResponse<ProfitLossDrilldownResult>>('/api/ProfitLoss/drilldown', {
    params: { ...toParams(q), lineKey, page, pageSize },
    timeout: 120000,
  });
  return unwrap(res.data);
};

/** Sunucu hata mesajını (Message) öne çıkarır. */
export const getErrorMessage = (err: unknown, fallback: string): string => {
  const anyErr = err as { response?: { data?: { Message?: string } }; message?: string };
  return anyErr?.response?.data?.Message ?? anyErr?.message ?? fallback;
};
